import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmAtlassianRequestTypeUpdateWorkflowPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { messages } from './messages';
import { UpdateRequestTypeWorkflowCardSuccess } from './UpdateRequestTypeWorkflowCardSuccess';

const styles = cssMap({
	outermostWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.150'),
		width: '100%',
		boxSizing: 'border-box',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	outerColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	titleSection_collapsedEnd: {
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	headerWorkItemIconWrap: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	contentSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		height: '104px',
		boxSizing: 'border-box',
		gap: token('space.150'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		overflowY: 'hidden',
	},
	contentSection_bodyExpanded: {
		height: 'auto',
		overflowY: 'visible',
		paddingBottom: token('space.500'),
	},
	contentSectionOuter: {
		position: 'relative',
		width: '100%',
		alignSelf: 'stretch',
	},
	fadeOffZone: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '32px',
		width: '100%',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	showMoreButton: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.input'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		zIndex: 100,
		opacity: 0,
		transition: 'opacity 0s',
		'&:hover': {
			opacity: 1,
			transition: 'opacity 0.15s ease-in-out',
			backgroundColor: token('color.background.input.hovered'),
		},
		'&:focus-visible': {
			opacity: 1,
			transition: 'opacity 0.15s ease-in-out',
			backgroundColor: token('color.background.input.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.input.pressed'),
		},
	},
	footerSection: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	headingWrapper: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		minWidth: '0px',
	},
	expandToggleWrap: {
		marginInlineStart: 'auto',
	},
	toggleZone_collapsed: {
		backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), ${token('color.background.input')} 100%)`,
	},
	toggleZone_expanded: {
		backgroundImage: 'none',
		backgroundColor: 'transparent',
	},
});

const BodyContentShowMoreToggle = ({
	isBodyExpanded,
	onToggle,
}: {
	isBodyExpanded: boolean;
	onToggle: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={cx(
				styles.fadeOffZone,
				isBodyExpanded ? styles.toggleZone_expanded : styles.toggleZone_collapsed,
			)}
		>
			<Pressable
				xcss={styles.showMoreButton}
				onClick={onToggle}
				testId={
					isBodyExpanded
						? 'update-request-type-workflow-card.collapse-body-content'
						: 'update-request-type-workflow-card.expand-body-content'
				}
			>
				{formatMessage(isBodyExpanded ? messages.collapseBodyContent : messages.expandBodyContent)}
			</Pressable>
		</Box>
	);
};

export type UpdateRequestTypeWorkflowCardProps = {
	payload: JsmAtlassianRequestTypeUpdateWorkflowPayload;
	onNext: () => void;
	isNextLoading?: boolean;
	isNextDisabled?: boolean;
	isSuccess?: boolean;
};

export const UpdateRequestTypeWorkflowCard = ({
	payload,
	onNext,
	isNextLoading = false,
	isNextDisabled = false,
	isSuccess = false,
}: UpdateRequestTypeWorkflowCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const [isBodyContentExpanded, setIsBodyContentExpanded] = useState(false);
	const [hasTruncatableBodyContent, setHasTruncatableBodyContent] = useState(false);
	const contentSectionRef = useRef<HTMLDivElement>(null);

	const ExpandCollapseIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const showExpandedBody = !isSuccess && isExpanded;

	const heading = formatMessage(messages.cardTitle);

	const measureBodyOverflow = useCallback(() => {
		const el = contentSectionRef.current;
		if (!el || isBodyContentExpanded) {
			return;
		}
		setHasTruncatableBodyContent(el.scrollHeight > el.clientHeight);
	}, [isBodyContentExpanded]);

	useEffect(() => {
		setIsBodyContentExpanded(false);
	}, [
		payload.requestTypeId,
		payload.requestTypeName,
		payload.targetIssueTypeId,
		payload.targetIssueTypeName,
	]);

	useLayoutEffect(() => {
		measureBodyOverflow();
	}, [
		measureBodyOverflow,
		showExpandedBody,
		payload.requestTypeId,
		payload.requestTypeName,
		payload.targetIssueTypeId,
		payload.targetIssueTypeName,
	]);

	useLayoutEffect(() => {
		const el = contentSectionRef.current;
		if (!el || typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver(() => {
			measureBodyOverflow();
		});
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [measureBodyOverflow]);

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />}>
			<Box xcss={styles.outermostWrapper} testId="update-request-type-workflow-card.root">
				{isSuccess ? (
					<UpdateRequestTypeWorkflowCardSuccess
						heading={heading}
						successMessage={formatMessage(messages.successPill, {
							requestTypeName: payload.requestTypeName,
						})}
					/>
				) : (
					<Box xcss={styles.outerColumn} testId="update-request-type-workflow-card">
						<Flex xcss={cx(styles.titleSection, !isExpanded && styles.titleSection_collapsedEnd)}>
							<Flex
								xcss={styles.headerWorkItemIconWrap}
								alignItems="center"
								justifyContent="center"
							>
								<WorkItemIcon label="" spacing="none" color={token('color.icon.accent.blue')} />
							</Flex>
							<Box xcss={styles.headingWrapper}>
								<Heading size="xsmall">{heading}</Heading>
							</Box>
							<Box xcss={styles.expandToggleWrap}>
								<IconButton
									icon={ExpandCollapseIcon}
									label={formatMessage(
										isExpanded ? messages.collapseBodyContent : messages.expandBodyContent,
									)}
									appearance="subtle"
									spacing="compact"
									onClick={() => setIsExpanded(!isExpanded)}
									testId="update-request-type-workflow-card.expand-toggle"
								/>
							</Box>
						</Flex>

						{showExpandedBody ? (
							<Box xcss={styles.contentSectionOuter}>
								<Box
									ref={contentSectionRef}
									xcss={cx(
										styles.contentSection,
										isBodyContentExpanded && styles.contentSection_bodyExpanded,
									)}
								>
									<Stack space="space.200">
										<Heading size="medium">{payload.requestTypeName}</Heading>
										<Text size="medium">
											{formatMessage(messages.workflowDescription, {
												workTypeName: payload.targetIssueTypeName,
												bold: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
											})}
										</Text>
									</Stack>
								</Box>
								{hasTruncatableBodyContent ? (
									<BodyContentShowMoreToggle
										isBodyExpanded={isBodyContentExpanded}
										onToggle={() => setIsBodyContentExpanded(!isBodyContentExpanded)}
									/>
								) : null}
							</Box>
						) : null}

						{showExpandedBody ? (
							<Flex xcss={styles.footerSection}>
								<Button
									appearance="primary"
									onClick={onNext}
									isLoading={isNextLoading}
									isDisabled={isNextDisabled}
									testId="update-request-type-workflow-card.next"
								>
									{formatMessage(messages.confirmButton)}
								</Button>
							</Flex>
						) : null}
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
